(() => {
  const ANCHOR_SELECTOR = '.cp-blog-shop-anchor[data-cp-shop="true"]';
  const SECTION_SELECTOR = '.cp-blog-shop';
  const MAX_PRODUCTS = 6;
  const SHOP_ROOT =
    (window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root) ||
    '/';
  const MONEY_FORMAT =
    (window.theme &&
      (window.theme.moneyFormat || window.theme.money_format)) ||
    (window.Shopify && window.Shopify.money_format) ||
    '${{amount}}';

  let infoPanelCounter = 0;

  const escapeHtml = (value) =>
    String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const stripHtml = (html) => {
    const container = document.createElement('div');
    container.innerHTML = html || '';
    container
      .querySelectorAll('style,script,noscript,template,svg')
      .forEach((node) => node.remove());
    const comments = [];
    const walker = document.createTreeWalker(
      container,
      NodeFilter.SHOW_COMMENT,
      null
    );
    while (walker.nextNode()) comments.push(walker.currentNode);
    comments.forEach((node) => node.remove());
    return (container.textContent || container.innerText || '')
      .replace(/\/\*[\s\S]*?\*\//g, ' ')
      .replace(/[{}[\]]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const sanitizeRichHtml = (html) => {
    const source = document.createElement('div');
    source.innerHTML = html || '';
    source
      .querySelectorAll(
        'script,style,noscript,iframe,object,embed,form,input,button,textarea,select,canvas,video,audio'
      )
      .forEach((node) => node.remove());

    const allowed = new Set([
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'span',
      'ul',
      'ol',
      'li',
      'a'
    ]);

    const elementWalker = document.createTreeWalker(
      source,
      NodeFilter.SHOW_ELEMENT,
      null
    );
    const unwrap = [];
    while (elementWalker.nextNode()) {
      const node = elementWalker.currentNode;
      const tag = node.tagName ? node.tagName.toLowerCase() : '';
      if (!allowed.has(tag)) {
        unwrap.push(node);
        continue;
      }

      const attrs = Array.from(node.attributes || []);
      for (let i = 0; i < attrs.length; i += 1) {
        const attrName = attrs[i].name.toLowerCase();
        if (attrName.startsWith('on') || attrName === 'style' || attrName === 'id' || attrName === 'class') {
          node.removeAttribute(attrs[i].name);
          continue;
        }
        if (tag === 'a') {
          if (!['href', 'title', 'target', 'rel'].includes(attrName)) {
            node.removeAttribute(attrs[i].name);
          }
          continue;
        }
        node.removeAttribute(attrs[i].name);
      }

      if (tag === 'a') {
        const href = node.getAttribute('href') || '';
        const safeHref = /^https?:\/\//i.test(href) || href.startsWith('/') || href.startsWith('#');
        if (!safeHref) {
          node.removeAttribute('href');
        }
        if (node.getAttribute('target') === '_blank') {
          node.setAttribute('rel', 'noopener noreferrer');
        }
      }
    }

    unwrap.forEach((node) => {
      const parent = node.parentNode;
      if (!parent) return;
      while (node.firstChild) parent.insertBefore(node.firstChild, node);
      parent.removeChild(node);
    });

    return source.innerHTML.trim();
  };

  const truncate = (text, limit) => {
    if (!text || text.length <= limit) return text || '';
    return text.slice(0, limit).replace(/\s+\S*$/, '') + '...';
  };

  const toHandles = (rawHandles) =>
    String(rawHandles || '')
      .split(',')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean)
      .slice(0, MAX_PRODUCTS);

  const resolveLayout = (requestedLayout, count) => {
    const layout = String(requestedLayout || 'auto').toLowerCase();
    if (layout === 'feature' || layout === 'duo' || layout === 'grid' || layout === 'carousel') {
      return layout;
    }
    if (count <= 1) return 'feature';
    if (count === 2) return 'duo';
    return 'grid';
  };

  const getPlacementRatio = (placement) => {
    if (placement === '1') return 0.33;
    if (placement === '2') return 0.5;
    if (placement === '3') return 0.75;
    return null;
  };

  const getArticleForShopBlock = (block) => {
    if (!block) return null;
    const page = block.closest('.t4s-main-article-page');
    if (page) {
      const articleInPage = page.querySelector('.t4s-article-content article');
      if (articleInPage) return articleInPage;
    }
    const previous = block.previousElementSibling;
    if (previous && previous.matches('.t4s-article-content')) {
      const articleInPrevious = previous.querySelector('article');
      if (articleInPrevious) return articleInPrevious;
    }
    return document.querySelector('.t4s-main-article-page .t4s-article-content article');
  };

  const placeShopBlockByParagraph = (block) => {
    if (!block) return;
    const placement = String(block.dataset.cpPlacement || '4').trim();
    if (placement === '4') return;

    const ratio = getPlacementRatio(placement);
    if (ratio == null) return;

    const article = getArticleForShopBlock(block);
    if (!article) return;

    const paragraphs = Array.from(article.querySelectorAll('p')).filter((paragraph) =>
      normalizeText(paragraph.textContent || '').length > 0
    );
    if (!paragraphs.length) return;

    const paragraphWeights = paragraphs.map((paragraph) => {
      const text = normalizeText(paragraph.textContent || '');
      return Math.max(1, text.length);
    });
    const totalWeight = paragraphWeights.reduce((sum, weight) => sum + weight, 0);
    if (!totalWeight) return;

    const targetWeight = totalWeight * ratio;
    let cumulative = 0;
    let bestIndex = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let index = 0; index < paragraphWeights.length; index += 1) {
      cumulative += paragraphWeights[index];
      const distance = Math.abs(cumulative - targetWeight);
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    }

    const target = paragraphs[bestIndex];
    if (!target || !target.parentNode) return;

    if (target.nextElementSibling === block) return;
    target.parentNode.insertBefore(block, target.nextSibling);
  };

  const positionArticleShopBlocks = (scope) => {
    const source = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    const blocks = source.querySelectorAll('.t4s-article-shop-block[data-cp-placement]');
    blocks.forEach((block) => placeShopBlockByParagraph(block));
  };

  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents || 0, MONEY_FORMAT);
    }
    const value = Number(cents || 0) / 100;
    return '$' + value.toFixed(2);
  };

  const getPrimaryVariant = (product) => {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (!variants.length) return null;
    const firstAvailable = variants.find((variant) => variant.available);
    return firstAvailable || variants[0];
  };

  const hasOnlyDefaultVariant = (product) => {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (variants.length !== 1) return false;
    const title = String(variants[0].title || '').toLowerCase();
    return title === '' || title === 'default title';
  };

  const createInfoPanelId = (handle) => {
    infoPanelCounter += 1;
    const key = String(handle || 'item').replace(/[^a-z0-9_-]/gi, '-');
    return `cp-blog-shop-info-${key}-${infoPanelCounter}`;
  };

  const buildCard = (product, layout) => {
    const primaryVariant = getPrimaryVariant(product);
    const isAvailable = Boolean(product.available && primaryVariant);
    const isDefaultOnly = hasOnlyDefaultVariant(product);
    const imageUrl =
      product.featured_image ||
      (Array.isArray(product.images) && product.images[0]) ||
      '';
    const descriptionLimit = layout === 'feature' ? 320 : 220;
    const description = truncate(
      stripHtml(product.body_html || product.description),
      descriptionLimit
    );
    const richInfo = sanitizeRichHtml(product.body_html || product.description);
    const productUrl = `${SHOP_ROOT}products/${product.handle}`;
    const title = escapeHtml(product.title || '');
    const price = formatMoney(primaryVariant ? primaryVariant.price : product.price);
    const compareAtPrice =
      primaryVariant &&
      primaryVariant.compare_at_price &&
      primaryVariant.compare_at_price > primaryVariant.price
        ? formatMoney(primaryVariant.compare_at_price)
        : '';
    const parsedRating = Number(product.rating || 0);
    const ratingValue = Number.isFinite(parsedRating) && parsedRating > 0
      ? Math.max(0, Math.min(5, parsedRating))
      : 5;
    const reviewCount = Math.max(0, Number(product.total_product_reviews || 0));
    const infoPanelId = createInfoPanelId(product.handle);
    const bagIcon = `
      <span class="cp-blog-shop__cta-icon" aria-hidden="true">
        <svg viewBox="0 0 19.081 17.254">
          <g transform="translate(-297.799 -406.372)">
            <path fill="currentColor" d="M308.577,422.193h-9.345V411.161H301.8v2.485h1.432v-2.482h3.9v2.482h1.432v-2.482h2.568v0h1.435v-1.436h-4v-3.359H301.8v3.359h-4v13.9h10.778ZM303.232,407.8h3.9v1.927h-3.9Z"></path>
            <path class="cp-spice-bag-plus" fill="currentColor" d="M316.88,417.824h-4.25v-4.25h-1.5v4.25h-4.25v1.5h4.25v4.25h1.5v-4.25h4.25Z"></path>
          </g>
        </svg>
      </span>
    `;
    const infoIcon = `
      <span class="cp-blog-shop__cta-icon cp-blog-shop__cta-icon--info" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M11 7h2V5h-2v2zm0 12h2v-8h-2v8zm1-17a10 10 0 1 1 0 20 10 10 0 0 1 0-20z"></path>
        </svg>
      </span>
    `;
    const closeIcon = `
      <span class="cp-blog-shop__cta-icon cp-blog-shop__cta-icon--close" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="8"></circle>
          <path d="M9 9l6 6M15 9l-6 6"></path>
        </svg>
      </span>
    `;

    let ctaHtml = `
      <button type="button" class="cp-blog-shop__street-tech-add cp-blog-shop__cta-side--add is-disabled" aria-disabled="true">
        ${bagIcon}
        <span class="cp-blog-shop__cta-label">OUT OF STOCK</span>
      </button>
    `;

    if (isAvailable) {
      if (isDefaultOnly) {
        ctaHtml = `
          <a
            href="${productUrl}"
            class="cp-blog-shop__street-tech-add cp-blog-shop__cta-side--add t4s-pr-item-btn t4s-pr-addtocart"
            data-action-atc
            data-variant-id="${primaryVariant.id}"
            data-default-label="ADD TO CART +"
            data-no-instant
          >
            ${bagIcon}
            <span class="cp-blog-shop__cta-label">ADD TO CART +</span>
          </a>
        `;
      } else {
        ctaHtml = `
          <a
            class="cp-blog-shop__street-tech-add cp-blog-shop__cta-side--add t4s-pr-item-btn t4s-pr-addtocart"
            href="${productUrl}"
            data-open-variant-picker="true"
          >
            ${bagIcon}
            <span class="cp-blog-shop__cta-label">SELECT OPTIONS</span>
          </a>
        `;
      }
    }

    return `
      <article class="cp-blog-shop__card cp-blog-shop__card--${layout}">
        <a class="cp-blog-shop__image-wrap" href="${productUrl}" aria-label="View ${title}">
          ${imageUrl ? `<img class="cp-blog-shop__image" src="${imageUrl}" alt="${title}" loading="lazy" width="900" height="900">` : ''}
        </a>
        <div class="cp-blog-shop__content">
          <div class="cp-blog-shop__top">
            <h4 class="cp-blog-shop__title"><a href="${productUrl}">${title}</a></h4>
            <div class="cp-blog-shop__price-wrap">
              <span class="cp-blog-shop__price">${price}</span>
              ${compareAtPrice ? `<span class="cp-blog-shop__compare">${compareAtPrice}</span>` : ''}
            </div>
          </div>
          <div class="cp-blog-shop__rating" aria-label="${ratingValue} out of 5 stars">
            <span class="cp-blog-shop__stars" aria-hidden="true">&#9733;&#9733;&#9733;&#9733;&#9733;</span>
            <span class="cp-blog-shop__reviews">(${Number.isFinite(reviewCount) ? reviewCount : 0})</span>
          </div>
          <div class="cp-blog-shop__description">${escapeHtml(description)}</div>
          <div class="cp-blog-shop__info-drawer" id="${infoPanelId}" data-info-drawer data-info-fallback="${escapeHtml(description || 'Product details coming soon.')}" hidden>
            <div class="cp-blog-shop__info-text">${richInfo || `<p>${escapeHtml(description || 'Product details coming soon.')}</p>`}</div>
          </div>
          <div class="cp-blog-shop__cta-wrap">
            <div class="cp-blog-shop__street-tech-actions" role="group" aria-label="${title} actions">
              ${ctaHtml}
              <a
                href="${productUrl}"
                class="cp-blog-shop__street-tech-info"
                aria-label="View details for ${title}"
              >
                ${infoIcon}
                ${closeIcon}
                <span class="cp-blog-shop__street-tech-info-label">INFO</span>
              </a>
            </div>
          </div>
        </div>
      </article>
    `;
  };

  const fetchProduct = async (handle) => {
    const url = `${SHOP_ROOT}products/${encodeURIComponent(handle)}.js`;
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) return null;
    const data = await response.json();
    if (!data || !data.handle) return null;
    return data;
  };

  const setInfoToggleLabel = (toggle, text) => {
    if (!toggle) return;
    const label = toggle.querySelector('[data-info-toggle-label]');
    if (label) label.textContent = text;
  };

  const trimLeadingEmptyBlocks = (container) => {
    if (!container) return;
    while (container.children && container.children.length) {
      const first = container.children[0];
      if (!first) break;
      if (normalizeText(first.textContent || '')) break;
      first.remove();
    }
  };

  const measureInfoDrawerHeight = (drawer) => {
    if (!drawer) return 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 900;
    const maxHeight = Math.max(180, Math.round(viewportHeight * 0.74));

    const previousHeight = drawer.style.height;
    const previousMaxHeight = drawer.style.maxHeight;
    const previousPaddingTop = drawer.style.paddingTop;
    const previousPaddingBottom = drawer.style.paddingBottom;
    const previousOverflow = drawer.style.overflowY;

    drawer.style.height = 'auto';
    drawer.style.maxHeight = 'none';
    drawer.style.paddingTop = '12px';
    drawer.style.paddingBottom = '12px';
    drawer.style.overflowY = 'visible';
    drawer.hidden = false;

    const measured = Math.ceil(drawer.scrollHeight);

    drawer.style.height = previousHeight;
    drawer.style.maxHeight = previousMaxHeight;
    drawer.style.paddingTop = previousPaddingTop;
    drawer.style.paddingBottom = previousPaddingBottom;
    drawer.style.overflowY = previousOverflow;

    return Math.max(140, Math.min(measured, maxHeight));
  };

  const updateInfoDrawerHeight = (card) => {
    if (!card) return;
    const drawer = card.querySelector('[data-info-drawer]');
    if (!drawer || drawer.hidden) return;
    const target = measureInfoDrawerHeight(drawer);
    if (target > 0) {
      card.style.setProperty('--cp-shop-open-info-h', `${target}px`);
    }
  };

  const hideInfoDrawerAfterTransition = (card, drawer) => {
    const hide = (event) => {
      if (event && event.target !== drawer) return;
      if (!card.classList.contains('is-info-open')) {
        drawer.hidden = true;
      }
      drawer.removeEventListener('transitionend', hide);
    };
    drawer.addEventListener('transitionend', hide);
    window.setTimeout(hide, 360);
  };

  const setInfoState = (card, shouldOpen) => {
    if (!card) return;
    const drawer = card.querySelector('[data-info-drawer]');
    const toggle = card.querySelector('[data-info-toggle]');
    if (!drawer || !toggle) return;

    if (shouldOpen) {
      const scope = card.closest(SECTION_SELECTOR) || document;
      const openCards = scope.querySelectorAll('.cp-blog-shop__card.is-info-open');
      openCards.forEach((openCard) => {
        if (openCard === card) return;
        setInfoState(openCard, false);
      });

      drawer.hidden = false;
      const infoText = drawer.querySelector('.cp-blog-shop__info-text');
      const fallback = drawer.getAttribute('data-info-fallback') || 'Product details coming soon.';
      if (infoText && !normalizeText(infoText.textContent || '')) {
        infoText.innerHTML = `<p>${escapeHtml(fallback)}</p>`;
      }
      if (infoText) trimLeadingEmptyBlocks(infoText);

      updateInfoDrawerHeight(card);
      card.classList.add('is-info-open');
      toggle.setAttribute('aria-expanded', 'true');
      setInfoToggleLabel(toggle, 'X');
      return;
    }

    card.classList.remove('is-info-open');
    toggle.setAttribute('aria-expanded', 'false');
    setInfoToggleLabel(toggle, 'INFO');
    card.style.removeProperty('--cp-shop-open-info-h');
    hideInfoDrawerAfterTransition(card, drawer);
  };

  const updateMobileControls = (section) => {
    if (!section) return;
    const controls = section.querySelector('[data-cp-shop-mobile-controls]');
    const track = section.querySelector('.cp-blog-shop__items');
    const prevBtn = controls ? controls.querySelector('[data-cp-shop-prev]') : null;
    const nextBtn = controls ? controls.querySelector('[data-cp-shop-next]') : null;
    const cards = track ? track.querySelectorAll('.cp-blog-shop__card') : [];
    if (!controls || !track) return;

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const isTablet = window.matchMedia('(min-width: 768px) and (max-width: 1199px)').matches;
    const layout = String(section.dataset.layout || '').toLowerCase();
    const layoutRequest = String(section.dataset.layoutRequest || '').toLowerCase();
    const countFromData = Number(section.dataset.count || cards.length || 0);
    const itemCount = Number.isFinite(countFromData) && countFromData > 0 ? countFromData : cards.length;
    const isExplicitCarousel = layout === 'carousel' || layoutRequest === 'carousel';
    const isDuo = layout === 'duo';
    const isGrid = layout === 'grid';
    const isMobileGridStack = isMobile && isGrid && itemCount > 2;
    const visibleCards = isDuo ? 1 : isMobile ? 1 : isTablet ? 2 : 3;
    let shouldShow = false;

    if (isMobileGridStack) {
      shouldShow = false;
    } else if (isDuo) {
      shouldShow = cards.length > 1;
    } else if (isExplicitCarousel) {
      shouldShow = cards.length > visibleCards;
    }
    controls.hidden = !shouldShow;
    controls.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    if (!shouldShow) {
      if (prevBtn) {
        prevBtn.disabled = true;
        prevBtn.hidden = true;
      }
      if (nextBtn) {
        nextBtn.disabled = true;
        nextBtn.hidden = true;
      }
      section.style.removeProperty('--cp-shop-mobile-image-h');
      return;
    }

    const firstImageWrap = track.querySelector('.cp-blog-shop__card .cp-blog-shop__image-wrap');
    if (firstImageWrap) {
      const imageHeight = Math.round(firstImageWrap.getBoundingClientRect().height);
      if (imageHeight > 0) {
        section.style.setProperty('--cp-shop-mobile-image-h', `${imageHeight}px`);
      }
    }

    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
    const scrollLeft = track.scrollLeft;
    const atStart = scrollLeft <= 2;
    const atEnd = scrollLeft >= maxScrollLeft - 2;
    if (prevBtn) {
      prevBtn.disabled = atStart;
      prevBtn.hidden = atStart;
    }
    if (nextBtn) {
      nextBtn.disabled = atEnd;
      nextBtn.hidden = atEnd;
    }
  };

  const setupMediaSlider = (card) => {
    if (!card) return;
    const slider = card.querySelector('[data-shop-media-slider]');
    if (!slider || slider.dataset.cpMediaBound === '1') return;
    slider.dataset.cpMediaBound = '1';

    const track = slider.querySelector('[data-shop-media-track]');
    const slides = track ? Array.from(track.querySelectorAll('[data-shop-media-slide]')) : [];
    const prevBtn = slider.querySelector('[data-shop-media-prev]');
    const nextBtn = slider.querySelector('[data-shop-media-next]');
    if (!track || slides.length <= 1) {
      if (prevBtn) prevBtn.hidden = true;
      if (nextBtn) nextBtn.hidden = true;
      return;
    }

    let index = 0;
    const maxIndex = slides.length - 1;

    const update = () => {
      track.style.transform = `translateX(-${index * 100}%)`;
      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index >= maxIndex;
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', (event) => {
        event.preventDefault();
        if (index <= 0) return;
        index -= 1;
        update();
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', (event) => {
        event.preventDefault();
        if (index >= maxIndex) return;
        index += 1;
        update();
      });
    }

    update();
  };

  const scrollMobileTrack = (section, direction) => {
    if (!section) return;
    const track = section.querySelector('.cp-blog-shop__items');
    if (!track) return;
    const firstCard = track.querySelector('.cp-blog-shop__card');
    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
    const step = Math.max(
      1,
      (firstCard ? firstCard.getBoundingClientRect().width : track.clientWidth) + gap
    );
    track.scrollBy({
      left: step * direction,
      behavior: 'smooth'
    });
    window.setTimeout(() => updateMobileControls(section), 280);
  };

  const setupShopSection = (section) => {
    if (!section || section.dataset.cpShopBound === '1') return;
    section.dataset.cpShopBound = '1';

    section
      .querySelectorAll('.cp-blog-shop__card--single-split')
      .forEach((card) => setupMediaSlider(card));

    const track = section.querySelector('.cp-blog-shop__items');
    if (track) {
      track.addEventListener(
        'scroll',
        () => updateMobileControls(section),
        { passive: true }
      );
    }

    const prevBtn = section.querySelector('[data-cp-shop-prev]');
    const nextBtn = section.querySelector('[data-cp-shop-next]');
    if (prevBtn) {
      prevBtn.addEventListener('click', (event) => {
        event.preventDefault();
        scrollMobileTrack(section, -1);
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', (event) => {
        event.preventDefault();
        scrollMobileTrack(section, 1);
      });
    }

    updateMobileControls(section);
  };

  const hydrateShopSections = (scope) => {
    const source = scope && typeof scope.querySelectorAll === 'function' ? scope : document;
    source.querySelectorAll(SECTION_SELECTOR).forEach((section) => setupShopSection(section));
  };

  const renderAnchor = async (anchor) => {
    if (anchor.dataset.cpShopMounted === '1') return;
    anchor.dataset.cpShopMounted = '1';

    const handles = toHandles(anchor.dataset.products);
    if (!handles.length) {
      anchor.style.display = 'none';
      return;
    }

    const heading = String(anchor.dataset.heading || '').trim();
    const subheading = String(anchor.dataset.subheading || '').trim();
    const requestedLayout = String(anchor.dataset.layout || 'auto').trim();
    const requestedLayoutNormalized = requestedLayout.toLowerCase();

    const fetched = await Promise.all(handles.map((handle) => fetchProduct(handle)));
    const products = fetched
      .filter((product) => Boolean(product && product.available))
      .slice(0, MAX_PRODUCTS);
    if (!products.length) {
      anchor.style.display = 'none';
      return;
    }

    const layout = resolveLayout(requestedLayout, products.length);
    const cardMarkup = products.map((product) => buildCard(product, layout)).join('');
    const headingMarkup =
      heading || subheading
        ? `
            <header class="cp-blog-shop__head">
              ${heading ? `<h3 class="cp-blog-shop__heading">${escapeHtml(heading)}</h3>` : ''}
              ${subheading ? `<p class="cp-blog-shop__subheading">${escapeHtml(subheading)}</p>` : ''}
            </header>
          `
        : '';

    const sectionMarkup = `
      <section class="cp-blog-shop cp-blog-shop--${layout}" data-count="${products.length}" data-layout="${layout}" data-layout-request="${requestedLayoutNormalized}">
        ${headingMarkup}
        <div class="cp-blog-shop__items cp-blog-shop__items--${layout}">
          ${cardMarkup}
        </div>
        <div class="cp-blog-shop__mobile-controls" data-cp-shop-mobile-controls aria-hidden="true">
          <button type="button" class="cp-blog-shop__mobile-control cp-blog-shop__mobile-control--prev" data-cp-shop-prev aria-label="Previous product">
            <span aria-hidden="true">&lt;</span>
          </button>
          <button type="button" class="cp-blog-shop__mobile-control cp-blog-shop__mobile-control--next" data-cp-shop-next aria-label="Next product">
            <span aria-hidden="true">&gt;</span>
          </button>
        </div>
      </section>
    `;

    const mount = document.createElement('div');
    mount.innerHTML = sectionMarkup.trim();
    const section = mount.firstElementChild;
    if (!section) {
      anchor.style.display = 'none';
      return;
    }
    const parent = anchor.parentNode;
    anchor.replaceWith(section);
    hydrateShopSections(parent || document);
  };

  const initAnchors = () => {
    const anchors = document.querySelectorAll(ANCHOR_SELECTOR);
    anchors.forEach((anchor) => {
      // Legacy inline anchor samples are retired in favor of metafield-driven blocks.
      if (anchor && typeof anchor.remove === 'function') {
        anchor.remove();
      } else if (anchor) {
        anchor.style.display = 'none';
      }
    });
  };

  const openMiniCart = () => {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    window.dispatchEvent(new CustomEvent('cart:updated'));

    const cartTrigger = document.querySelector(
      '.t4s-site-nav__cart [data-drawer-options*="#t4s-mini_cart"], [data-drawer-options*="#t4s-mini_cart"]'
    );
    if (cartTrigger) {
      cartTrigger.dispatchEvent(
        new MouseEvent('click', { bubbles: true, cancelable: true })
      );
      return;
    }

    const drawer = document.querySelector('#t4s-mini_cart, .cart-drawer, #CartDrawer');
    if (!drawer) return;
    drawer.classList.remove('t4s-dn', 'is-hidden', 'hidden');
    drawer.classList.add('is-open', 'active', 'drawer--is-open');
    drawer.setAttribute('aria-hidden', 'false');
  };

  const bindGlobalHandlers = () => {
    if (document.documentElement.dataset.cpBlogGlobalBound === '1') return;
    document.documentElement.dataset.cpBlogGlobalBound = '1';

    document.addEventListener('click', async (event) => {
      const infoToggle = event.target.closest('[data-info-toggle]');
      if (infoToggle && infoToggle.closest(SECTION_SELECTOR)) {
        event.preventDefault();
        const card = infoToggle.closest('.cp-blog-shop__card');
        if (!card) return;
        const shouldOpen = !card.classList.contains('is-info-open');
        setInfoState(card, shouldOpen);
        return;
      }

      const trigger = event.target.closest(
        '.cp-blog-shop__street-tech-add[data-action-atc][data-variant-id]'
      );
      if (trigger) {
        event.preventDefault();
        if (trigger.dataset.cpBusy === '1') return;

        const variantId = Number(trigger.getAttribute('data-variant-id'));
        if (!Number.isFinite(variantId) || variantId <= 0) {
          window.location.href = trigger.getAttribute('href') || `${SHOP_ROOT}cart`;
          return;
        }

        const label = trigger.querySelector('.cp-blog-shop__cta-label');
        const previousLabel = label ? label.textContent : '';
        trigger.dataset.cpBusy = '1';
        trigger.classList.add('is-working');
        if (label) label.textContent = 'ADDING...';

        try {
          const response = await fetch(`${SHOP_ROOT}cart/add.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ id: variantId, quantity: 1 })
          });
          if (!response.ok) throw new Error(`ATC failed (${response.status})`);
          await response.json();
          openMiniCart();
        } catch (error) {
          window.location.href = trigger.getAttribute('href') || `${SHOP_ROOT}cart`;
        } finally {
          trigger.dataset.cpBusy = '0';
          trigger.classList.remove('is-working');
          if (label) label.textContent = previousLabel || 'ADD TO CART +';
        }
        return;
      }

      if (!event.target.closest('.cp-blog-shop__card')) {
        document
          .querySelectorAll('.cp-blog-shop__card.is-info-open')
          .forEach((card) => setInfoState(card, false));
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document
        .querySelectorAll('.cp-blog-shop__card.is-info-open')
        .forEach((card) => setInfoState(card, false));
    });
  };

  const bindResizeHandler = () => {
    if (window.__cpBlogResizeBound) return;
    window.__cpBlogResizeBound = true;
    let resizeTimer = null;
    window.addEventListener('resize', () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        document
          .querySelectorAll('.cp-blog-shop__card.is-info-open')
          .forEach((card) => updateInfoDrawerHeight(card));
        document
          .querySelectorAll(SECTION_SELECTOR)
          .forEach((section) => updateMobileControls(section));
      }, 120);
    });
  };

  const init = () => {
    hydrateShopSections(document);
    positionArticleShopBlocks(document);
    initAnchors();
    window.setTimeout(() => {
      positionArticleShopBlocks(document);
      hydrateShopSections(document);
    }, 80);
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        init();
        bindGlobalHandlers();
        bindResizeHandler();
      },
      { once: true }
    );
  } else {
    init();
    bindGlobalHandlers();
    bindResizeHandler();
  }
})();
